/**
 * The guide's light rig.
 *
 * IWSDK creates a world with **no lights and no environment map**. It registers
 * the light components and an `EnvironmentSystem`, but neither does anything
 * until something adds a component — so a PBR model dropped into passthrough is
 * lit by nothing and renders pure black. Nothing in the SDK warns about this;
 * the model simply arrives as a silhouette.
 *
 * ## Why plain three.js and not IWSDK's light components
 *
 * The first version of this used `DirectionalLightComponent` and `IBLTexture`.
 * That path is provably correct in isolation — a harness driving IWSDK's real
 * `LightSystem` puts a correctly configured light in the scene — and the guide
 * still rendered black on-device. Rather than keep guessing across layers that
 * cannot be observed from here, the rig now owns three plain three.js lights
 * and sets `scene.environment` itself. No ECS components, no entity transforms,
 * no visibility proxies, no `LevelRoot` marker, no per-frame binding.
 *
 * That also buys a second illumination path with different failure modes:
 * lights are collected by traversing the scene graph, whereas `scene.environment`
 * is applied per material at draw time. If a graph problem were eating the
 * lights, image-based lighting would still reach the model.
 *
 * Nothing here throws. Lighting failing must not take the guide with it, and
 * every step reports itself to the log — being unable to see why the scene was
 * black is what made the first attempt cost a headset session.
 */
import type { World } from '@iwsdk/core';
import {
  DirectionalLight,
  HemisphereLight,
  Mesh,
  Object3D,
  PMREMGenerator,
  Quaternion,
  Vector3,
  type Light,
  type Scene,
  type WebGLRenderer,
} from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

import { uiLog } from '../ui/uiLog.js';
import type { AvatarLighting, DirectionalLightSpec } from './profile.js';

/** Far enough that the light reads as parallel across a room. */
const KEY_DISTANCE_METRES = 8;

export interface LightRig {
  /**
   * Aim the rig along the guide's facing. Pass the model's **world**
   * quaternion every frame.
   */
  setFacing(facing: Quaternion): void;
  /** Swap in a different profile's rig, once the avatar manifest has loaded. */
  apply(lighting: AvatarLighting): void;
  dispose(): void;
}

interface Aimed {
  light: DirectionalLight;
  /** Direction of travel in avatar-local space. */
  direction: Vector3;
}

export function installLighting(world: World, lighting: AvatarLighting): LightRig {
  const scene = world.scene;
  const owned: Object3D[] = [];
  let aimed: Aimed[] = [];
  let environment: { dispose(): void } | null = null;

  const clear = (): void => {
    for (const object of owned) {
      object.removeFromParent();
      (object as Partial<Light>).dispose?.();
    }
    owned.length = 0;
    aimed = [];
  };

  const build = (next: AvatarLighting): void => {
    clear();
    try {
      aimed = [
        addDirectional(scene, owned, 'guide-key-light', next.key),
        addDirectional(scene, owned, 'guide-fill-light', next.fill),
      ].filter((entry): entry is Aimed => entry !== null);

      if (next.ambient.intensity > 0) {
        const hemisphere = new HemisphereLight(
          next.ambient.skyColor,
          next.ambient.groundColor,
          next.ambient.intensity,
        );
        hemisphere.name = 'guide-ambient-light';
        scene.add(hemisphere);
        owned.push(hemisphere);
      }

      uiLog.push(
        `lighting: ${owned.length} lights (key ${next.key.intensity}, ` +
          `fill ${next.fill.intensity}, ambient ${next.ambient.intensity})`,
      );
    } catch (error) {
      uiLog.push(`lighting FAILED: ${describeError(error)}`);
    }
  };

  build(lighting);
  environment = installEnvironment(scene, world.renderer, lighting);

  const composed = new Quaternion();
  const offset = new Vector3();

  return {
    setFacing(facing) {
      for (const { light, direction } of aimed) {
        // A directional light shines from its position toward its target, so
        // the position is the direction of travel negated.
        offset.copy(direction).applyQuaternion(composed.copy(facing));
        light.position.copy(offset).multiplyScalar(-KEY_DISTANCE_METRES);
      }
    },
    apply(next) {
      build(next);
      environment?.dispose();
      environment = installEnvironment(scene, world.renderer, next);
    },
    dispose() {
      clear();
      environment?.dispose();
      environment = null;
    },
  };
}

function addDirectional(
  scene: Scene,
  owned: Object3D[],
  name: string,
  spec: DirectionalLightSpec,
): Aimed | null {
  if (spec.intensity <= 0) return null;

  const light = new DirectionalLight(spec.color, spec.intensity);
  light.name = name;
  scene.add(light);
  // Three reads the direction from `position - target.position`, and the
  // target's world matrix is only updated if it is in the scene.
  scene.add(light.target);
  owned.push(light, light.target);

  const direction = new Vector3(...spec.direction).normalize();
  // Aim it once now, so the rig is correct on the very first frame rather than
  // only after the avatar has loaded and started reporting its facing.
  light.position.copy(direction).multiplyScalar(-KEY_DISTANCE_METRES);
  return { light, direction };
}

/**
 * Image-based lighting, written straight to `scene.environment`.
 *
 * IWSDK's `EnvironmentSystem` would do this from an `IBLTexture` component, but
 * only for an entity that also carries `LevelRoot` — and the default level root
 * created during world init never receives that marker. Doing it here removes
 * that trap. The system's own teardown only clears a target it created itself,
 * so it will not fight this.
 *
 * `RoomEnvironment` is generated in memory: neutral studio lighting with no
 * asset to ship and no network fetch.
 */
function installEnvironment(
  scene: Scene,
  renderer: WebGLRenderer,
  lighting: AvatarLighting,
): { dispose(): void } | null {
  if (lighting.environment !== 'room' || lighting.environmentIntensity <= 0) {
    if (lighting.environment !== 'none' && lighting.environment !== 'room') {
      uiLog.push(`lighting: environment "${lighting.environment}" is not supported — use "room"`);
    }
    return null;
  }

  let pmrem: PMREMGenerator | undefined;
  try {
    pmrem = new PMREMGenerator(renderer);
    const room = new RoomEnvironment();
    const target = pmrem.fromScene(room, 0.04);
    disposeRoom(room);

    scene.environment = target.texture;
    scene.environmentIntensity = lighting.environmentIntensity;
    uiLog.push(`lighting: room IBL at ${lighting.environmentIntensity}`);

    return {
      dispose() {
        if (scene.environment === target.texture) scene.environment = null;
        target.dispose();
      },
    };
  } catch (error) {
    // The directional lights alone still illuminate the guide; only
    // reflections are lost.
    uiLog.push(`lighting: room IBL failed — ${describeError(error)}`);
    return null;
  } finally {
    pmrem?.dispose();
  }
}

function describeError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** `RoomEnvironment.dispose()` exists in some three versions and not others. */
function disposeRoom(room: Scene): void {
  const disposable = room as unknown as { dispose?: () => void };
  if (typeof disposable.dispose === 'function') {
    disposable.dispose();
    return;
  }
  room.traverse((object) => {
    if (object instanceof Mesh) {
      object.geometry.dispose();
      for (const material of Array.isArray(object.material) ? object.material : [object.material]) {
        material.dispose();
      }
    }
  });
}

/**
 * What is actually in the scene, in one log line.
 *
 * Written for the case this module already lost once: the guide renders as a
 * black silhouette and there is no way to tell whether the lights were never
 * created, were created somewhere the renderer never traverses, or are fine and
 * the fault is in the model. Reported to the on-page log, which is readable the
 * moment the session ends.
 */
export function describeLighting(world: World, avatarRoot?: Object3D): string {
  const scene = world.scene;
  const lights: string[] = [];
  scene.traverse((object) => {
    const light = object as Partial<Light> & Object3D;
    if (light.isLight) lights.push(`${object.type}@${light.intensity ?? '?'}`);
  });

  const parts = [
    `lights=${lights.length}${lights.length > 0 ? ` [${lights.join(' ')}]` : ''}`,
    `env=${scene.environment ? `yes@${scene.environmentIntensity}` : 'NO'}`,
  ];

  if (avatarRoot) {
    // The check that reasoning cannot settle from here: a model that is not
    // under the scene being rendered can never see any of the above.
    let root: Object3D = avatarRoot;
    while (root.parent) root = root.parent;
    parts.push(`avatarInScene=${root === (scene as Object3D) ? 'yes' : 'NO'}`);

    let materials = 0;
    let sample = '';
    avatarRoot.traverse((object) => {
      if (!(object instanceof Mesh)) return;
      for (const material of Array.isArray(object.material) ? object.material : [object.material]) {
        materials += 1;
        if (sample) continue;
        const m = material as { type: string; opacity?: number; transparent?: boolean };
        sample = `${m.type} opacity=${m.opacity ?? '?'} transparent=${String(m.transparent)}`;
      }
    });
    parts.push(`materials=${materials}`, sample);
  }

  return parts.filter(Boolean).join(' · ');
}
